import { useState } from "react";
import { User, Mail, Loader2 } from "lucide-react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { PhoneInputEmpire, getFullPhoneNumber } from "./PhoneInputEmpire";
import { sanitizeName } from "../lib/inputSanitizers";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Reusable payment details form.
 *
 * Props:
 *   onSubmit(formData) – called with validated data:
 *     { guest_first_name, guest_last_name, guest_name,
 *       guest_email, guest_phone, paymentMethod }
 *   submitting – boolean
 *   amount – number (for the CTA label)
 *   buttonLabel – optional override for button text
 */
export const PaymentDetailsForm = ({
  onSubmit,
  submitting = false,
  amount,
  buttonLabel,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+254");
  const [phoneValid, setPhoneValid] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("mpesa");
  const [formTouched, setFormTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  /* ─── derived validity ───────────────────────────────────────── */
  const emailValid = EMAIL_RE.test(email);

  const formReady =
    firstName.trim() &&
    lastName.trim() &&
    emailValid &&
    phoneValid &&
    termsAccepted &&
    paymentMethod === "mpesa"; // card not selectable yet

  /* ─── validation messages on blur ───────────────────────────── */
  const validateEmail = () => {
    if (!email) setEmailError("Email is required");
    else if (!emailValid) setEmailError("Enter a valid email address");
    else setEmailError("");
  };

  /* ─── submit handler ─────────────────────────────────────────── */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formReady || submitting) return;
    onSubmit({
      guest_first_name: firstName.trim(),
      guest_last_name: lastName.trim(),
      guest_name: `${firstName.trim()} ${lastName.trim()}`,
      guest_email: email.trim().toLowerCase(),
      guest_phone: getFullPhoneNumber(countryCode, phone),
      paymentMethod,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* ── First + Last Name ─────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        {/* First Name */}
        <div className="space-y-1.5">
          <Label htmlFor="firstName" className="text-white text-sm">
            First Name <span className="text-red-400">*</span>
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            <Input
              id="firstName"
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(sanitizeName(e.target.value))}
              onBlur={() => setFormTouched((t) => ({ ...t, firstName: true }))}
              className="input-empire pl-9 h-10 text-sm"
              required
            />
          </div>
          {formTouched.firstName && !firstName.trim() && (
            <p className="text-xs text-red-400">First name is required</p>
          )}
        </div>

        {/* Last Name */}
        <div className="space-y-1.5">
          <Label htmlFor="lastName" className="text-white text-sm">
            Last Name <span className="text-red-400">*</span>
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
            <Input
              id="lastName"
              type="text"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(sanitizeName(e.target.value))}
              onBlur={() => setFormTouched((t) => ({ ...t, lastName: true }))}
              className="input-empire pl-9 h-10 text-sm"
              required
            />
          </div>
          {formTouched.lastName && !lastName.trim() && (
            <p className="text-xs text-red-400">Last name is required</p>
          )}
        </div>
      </div>

      {/* ── Email ────────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <Label htmlFor="email" className="text-white text-sm">
          Email Address <span className="text-red-400">*</span>
        </Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
          <Input
            id="email"
            type="email"
            placeholder="john@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            onBlur={() => {
              setFormTouched((t) => ({ ...t, email: true }));
              validateEmail();
            }}
            className={`input-empire pl-9 h-10 text-sm ${
              formTouched.email && emailError ? "border-red-500" : ""
            } ${formTouched.email && !emailError && email ? "border-green-500" : ""}`}
            required
          />
          {formTouched.email && !emailError && email && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <svg className="w-4 h-4 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </div>
        {emailError && (
          <p className="text-xs text-red-400">{emailError}</p>
        )}
        {!emailError && (
          <p className="text-xs text-neutral-500">
            Your booking confirmation and QR code will be sent here
          </p>
        )}
      </div>

      {/* ── Phone ────────────────────────────────────────────────── */}
      <div className="space-y-1.5">
        <Label className="text-white text-sm">
          Phone Number <span className="text-red-400">*</span>
        </Label>
        <PhoneInputEmpire
          value={phone}
          onChange={(val) => {
            setPhone(val);
            if (!formTouched.phone && val) setFormTouched((t) => ({ ...t, phone: true }));
          }}
          countryCode={countryCode}
          onCountryCodeChange={setCountryCode}
          onValidChange={setPhoneValid}
          touched={formTouched.phone}
          placeholder="712 345 678"
        />
        {phone && !phoneValid && (
          <p className="text-xs text-red-400">
            {countryCode === "+254" 
              ? "Invalid Kenyan phone number format" 
              : "Enter a valid phone number"}
          </p>
        )}
        {phoneValid && (
          <p className="text-xs text-green-500">Phone number looks good!</p>
        )}
        {!phone && (
          <p className="text-xs text-neutral-500">
            This number will receive the M-Pesa payment prompt
          </p>
        )}
      </div>

      {/* ── Payment Method ───────────────────────────────────────── */}
      <div className="space-y-3 pt-1">
        <Label className="text-white text-sm">Payment Method</Label>
        <div className="grid grid-cols-2 gap-3">
          {/* M-Pesa */}
          <button
            type="button"
            onClick={() => setPaymentMethod("mpesa")}
            className={`relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
              paymentMethod === "mpesa"
                ? "border-empire-gold bg-empire-gold/10 shadow-[0_0_20px_rgba(215,172,4,0.2)]"
                : "border-neutral-700 hover:border-neutral-500 bg-neutral-900/50"
            }`}
          >
            {paymentMethod === "mpesa" && (
              <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-empire-gold flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-black" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            <img
              src="/branding/mpesa.png"
              alt="M-Pesa"
              className="h-8 object-contain"
            />
            <span className={`text-xs font-semibold ${paymentMethod === "mpesa" ? "text-empire-gold" : "text-neutral-400"}`}>
              M-Pesa
            </span>
          </button>

          {/* Card – coming soon */}
          <div className="relative">
            <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-neutral-800 bg-neutral-900/30 blur-[2px] pointer-events-none select-none">
              <img
                src="/branding/card.png"
                alt="Card"
                className="h-8 object-contain opacity-50"
              />
              <span className="text-xs font-semibold text-neutral-600">Card</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center rounded-xl">
              <span className="bg-neutral-900/90 text-neutral-400 text-xs font-semibold px-3 py-1.5 rounded-full border border-neutral-700">
                Coming soon
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Terms & Conditions ───────────────────────────────────── */}
      <div className="flex items-start gap-3 pt-1">
        <Checkbox
          id="terms"
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(!!checked)}
          className="mt-0.5 border-neutral-600 data-[state=checked]:bg-empire-gold data-[state=checked]:border-empire-gold data-[state=checked]:text-black"
        />
        <Label htmlFor="terms" className="text-sm text-neutral-300 leading-relaxed cursor-pointer">
          I agree to the{" "}
          <a
            href="http://empiredancekenya.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-empire-gold underline underline-offset-2 hover:text-yellow-400 transition-colors"
          >
            Terms &amp; Conditions
          </a>{" "}
          and{" "}
          <a
            href="http://empiredancekenya.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-empire-gold underline underline-offset-2 hover:text-yellow-400 transition-colors"
          >
            Privacy Policy
          </a>
        </Label>
      </div>

      {/* ── Submit ───────────────────────────────────────────────── */}
      <Button
        type="submit"
        disabled={!formReady || submitting}
        className="w-full btn-empire h-14 text-base font-semibold disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing…
          </>
        ) : (
          buttonLabel ||
          (amount
            ? `Pay KES ${Number(amount).toLocaleString()} via M-Pesa`
            : "Pay via M-Pesa")
        )}
      </Button>

      {!termsAccepted && (
        <p className="text-xs text-center text-neutral-600">
          Please accept the terms and conditions to continue
        </p>
      )}
    </form>
  );
};
